import type { GeneratedSpecial } from '../components/specials/SpecialCard';

// Custom error class with toString method to prevent [object Object] errors
class ApiError {
  message: string;
  details?: any;
  
  constructor(message: string, details?: any) {
    this.message = message;
    this.details = details;
  }
  
  toString(): string {
    if (this.details) {
      return `${this.message}: ${typeof this.details === 'object' ? JSON.stringify(this.details) : this.details}`;
    }
    return this.message;
  }
}

export type ToneOption = 'casual' | 'formal' | 'hip';
export type FormatOption = 'menu' | 'intro-bullets' | 'paragraphs';

export interface GenerateSpecialRequest {
  restaurantUrl: string;
  context?: string;
  includeEmojis: boolean;
  customImage?: File;
  useCustomImage?: boolean;
  useDefaultImage?: boolean;
  tone: ToneOption;
  format: FormatOption;
}

export interface GenerateSpecialResponse {
  name: string;
  description: string;
  imageUrl?: string;
  error?: string;
}

/**
 * Calls the API to generate a restaurant special
 * @param data Request data containing restaurant URL and theme
 * @returns Generated special with name, description, and optional image URL
 */
export async function generateSpecial(data: GenerateSpecialRequest): Promise<GeneratedSpecial> {
  try {
    // In development, we'll use a mock response for testing
    if (import.meta.env.DEV && !import.meta.env.VITE_USE_REAL_API) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      return getMockSpecial();
    }

    // In production, call the actual API endpoint
    let response;
    
    // If we have a custom image, we need to use FormData instead of JSON
    if (data.useCustomImage && data.customImage) {
      const formData = new FormData();
      formData.append('restaurantUrl', data.restaurantUrl);
      if (data.context) formData.append('context', data.context);
      formData.append('includeEmojis', String(data.includeEmojis));
      formData.append('useCustomImage', 'true');
      formData.append('useDefaultImage', String(data.useDefaultImage || false));
      formData.append('customImage', data.customImage);
      formData.append('tone', data.tone);
      formData.append('format', data.format);
      
      response = await fetch('/api/generate', {
        method: 'POST',
        body: formData,
      });
    } else {
      // Regular JSON request without custom image
      response = await fetch('/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          restaurantUrl: data.restaurantUrl,
          context: data.context,
          includeEmojis: data.includeEmojis,
          useCustomImage: false,
          useDefaultImage: data.useDefaultImage || false,
          tone: data.tone,
          format: data.format
        }),
      });
    }

    if (!response.ok) {
      let errorMessage = 'Failed to generate special';
      let errorDetails;

      try {
        const errorData = await response.json();
        if (errorData && typeof errorData === 'object' && 'error' in errorData) {
          errorMessage = String(errorData.error);
          errorDetails = errorData;
        }
      } catch {
        try {
          errorMessage = await response.text();
        } catch {}
      }

      throw new ApiError(errorMessage, errorDetails);
    }

    return await response.json() as GeneratedSpecial;
  } catch (error) {
    console.error('Error generating special:', error);
    if (error instanceof ApiError) {
      throw error;
    } else if (error instanceof Error) {
      throw new ApiError(error.message, error);
    } else {
      throw new ApiError(typeof error === 'string' ? error : 'Unknown error', error);
    }
  }
}

/**
 * Generates a mock special for development and testing
 * @returns A mock generated special
 */
function getMockSpecial(): GeneratedSpecial {
  return {
    name: 'Summer Sunset Risotto',
    description: 'Creamy saffron risotto with grilled summer vegetables and fresh herbs, finished with a splash of white wine and parmesan.',
    imageUrl: 'https://images.unsplash.com/photo-1476124369491-e7addf5db371?q=80&w=2070&auto=format&fit=crop',
  };
}
